import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Device, Member, NfcCardImportReport, NfcCardList, NfcEnrolment, page } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, CheckboxField, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { Dialog } from '../fields-notices/Dialog';
import { NFC, useGroupOptions } from './api';
import { CARDS_KEY, CardActions, CardStatus, hintText } from './nfcCommon';

const PAGE_SIZE = 20;
const DeviceList = z.array(Device);
const MemberPage = page(Member);
type Status = 'all' | 'active' | 'blocked' | 'lost' | 'retired';

/** Reads a chosen file as text. */
function readText(file: File): Promise<string> {
  return file.text();
}

export function ImportDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [readError, setReadError] = useState('');
  const run = useMutation({
    mutationFn: () => api.post(`${NFC}/import`, { body: { csv }, schema: NfcCardImportReport }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CARDS_KEY }),
  });
  const report = run.data;
  return (
    <Dialog title={t('w18.nfc.import.title')} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-700">{t('w18.nfc.import.help')}</p>
        <div>
          <label htmlFor="w18-import-file" className="mb-1 block text-sm font-medium text-slate-800">
            {t('w18.nfc.import.file')}
          </label>
          <input
            id="w18-import-file"
            type="file"
            accept=".csv,text/csv,text/plain"
            className="block w-full text-sm"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setReadError('');
              run.reset();
              readText(f)
                .then((text) => {
                  setCsv(text);
                  setFileName(f.name);
                })
                .catch(() => setReadError(t('w18.nfc.import.unreadable')));
            }}
          />
          {fileName ? <p className="mt-1 text-xs text-slate-600">{t('w18.nfc.import.chosen', { name: fileName })}</p> : null}
        </div>
        <div>
          <label htmlFor="w18-import-text" className="mb-1 block text-sm font-medium text-slate-800">
            {t('w18.nfc.import.paste')}
          </label>
          <textarea
            id="w18-import-text"
            rows={5}
            className="block w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-xs"
            value={csv}
            onChange={(e) => {
              setCsv(e.target.value);
              run.reset();
            }}
          />
        </div>
        {readError ? <Alert>{readError}</Alert> : null}
        <ErrorLine error={run.error} />
        {report ? (
          <div aria-live="polite" className="space-y-2">
            <Alert kind={report.skipped.length === 0 ? 'success' : 'info'}>
              {t('w18.nfc.import.result', {
                added: report.added,
                total: report.total,
                skipped: report.skipped.length,
              })}
            </Alert>
            {report.skipped.length > 0 ? (
              <TableWrap caption={t('w18.nfc.import.skipped')}>
                <thead className="bg-slate-50">
                  <tr>
                    <Th>{t('w18.nfc.import.row')}</Th>
                    <Th>{t('w18.nfc.import.reason')}</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.skipped.map((s) => (
                    <tr key={s.row}>
                      <td className="px-4 py-2">{s.row}</td>
                      <td className="px-4 py-2">{t(s.reasonKey)}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            ) : null}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{report ? t('w18.nfc.close') : t('common.cancel')}</Button>
          <Button variant="primary" disabled={run.isPending || csv.trim() === ''} onClick={() => run.mutate()}>
            {t('w18.nfc.import.button')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Whole seconds left until an ISO time (never negative). */
export function secondsLeft(iso: string, now = Date.now()): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - now) / 1000));
}

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function Progress({ enrolment, onCancel, cancelling }: { enrolment: NfcEnrolment; onCancel: () => void; cancelling: boolean }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const done = enrolment.items.filter((i) => i.cardId).length;
  const open = enrolment.status === 'open';
  return (
    <div className="space-y-4" aria-live="polite">
      <Alert kind={enrolment.status === 'done' ? 'success' : open ? 'info' : 'error'}>
        {open
          ? t('w18.nfc.enrol.waiting', {
              time: clock(secondsLeft(enrolment.expiresAt, now)),
            })
          : t(`w18.nfc.enrol.ended.${enrolment.status}`)}
      </Alert>
      <p className="text-sm text-slate-700">{t('w18.nfc.enrol.progress', { done, total: enrolment.items.length })}</p>
      <progress
        className="h-2 w-full"
        max={enrolment.items.length}
        value={done}
        aria-label={t('w18.nfc.enrol.progress', {
          done,
          total: enrolment.items.length,
        })}
      />
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
        {enrolment.items.map((i) => (
          <li key={i.memberId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
            <span>{i.memberName}</span>
            {i.cardId ? (
              <Badge tone="good">{t('w18.nfc.enrol.registered')}</Badge>
            ) : i.error ? (
              <Badge tone="bad">{t(i.error)}</Badge>
            ) : (
              <Badge>{t('w18.nfc.enrol.waitingItem')}</Badge>
            )}
          </li>
        ))}
      </ul>
      {open ? (
        <div className="flex justify-end">
          <Button variant="danger" disabled={cancelling} onClick={onCancel}>
            {t('w18.nfc.enrol.cancel')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** "Enrol cards on a kiosk": choose a kiosk and members, then watch the cards come in (contract nfc-cards s4). */
export function EnrolDialog({ onClose, initialGroupId }: { onClose: () => void; initialGroupId?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const groups = useGroupOptions();
  const devices = useQuery({
    queryKey: ['w18', 'nfc', 'kiosks'],
    queryFn: () => api.get('/kiosk-devices', { schema: DeviceList }),
  });
  const [deviceId, setDeviceId] = useState('');
  const [groupId, setGroupId] = useState(initialGroupId ?? '');
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [enrolmentId, setEnrolmentId] = useState<string | null>(null);
  const kiosks = (devices.data ?? []).filter((d) => d.kind === 'kiosk' && d.status === 'active');
  const members = useQuery({
    queryKey: ['w18', 'nfc', 'members', groupId, search],
    queryFn: () =>
      api.get('/members', {
        query: {
          groupId,
          status: 'current',
          search: search || undefined,
          pageSize: 100,
        },
        schema: MemberPage,
      }),
    enabled: groupId !== '',
    placeholderData: keepPreviousData,
  });
  const start = useMutation({
    mutationFn: () =>
      api.post(`${NFC}/enrolments`, {
        body: { deviceId, memberIds: Object.keys(chosen) },
        schema: NfcEnrolment,
      }),
    onSuccess: (e) => {
      setEnrolmentId(e.id);
      qc.setQueryData(['w18', 'nfc', 'enrolment', e.id], e);
    },
  });
  const enrolment = useQuery({
    queryKey: ['w18', 'nfc', 'enrolment', enrolmentId],
    queryFn: () => api.get(`${NFC}/enrolments/${enrolmentId}`, { schema: NfcEnrolment }),
    enabled: enrolmentId !== null,
    refetchInterval: (q) => (q.state.data?.status === 'open' ? 2000 : false),
  });
  const cancel = useMutation({
    mutationFn: () =>
      api.post(`${NFC}/enrolments/${enrolmentId}/cancel`, {
        schema: NfcEnrolment,
      }),
    onSuccess: (e) => {
      qc.setQueryData(['w18', 'nfc', 'enrolment', e.id], e);
      toast(t('w18.nfc.enrol.cancelled'));
    },
  });
  const finished = enrolment.data !== undefined && enrolment.data.status !== 'open';
  useEffect(() => {
    if (finished) void qc.invalidateQueries({ queryKey: CARDS_KEY });
  }, [finished, qc]);
  const count = Object.keys(chosen).length;
  return (
    <Dialog title={t('w18.nfc.enrol.title')} onClose={onClose} wide>
      {enrolmentId && enrolment.data ? (
        <div className="space-y-4">
          <Progress enrolment={enrolment.data} onCancel={() => cancel.mutate()} cancelling={cancel.isPending} />
          <ErrorLine error={enrolment.error ?? cancel.error} />
          {finished ? (
            <div className="flex justify-end">
              <Button variant="primary" onClick={onClose}>
                {t('w18.nfc.close')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-700">{t('w18.nfc.enrol.help')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField label={t('w18.nfc.enrol.kiosk')} value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
              <option value="">{t('w18.nfc.enrol.chooseKiosk')}</option>
              {kiosks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.online ? '' : ` (${t('w18.nfc.enrol.offline')})`}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={t('w18.nfc.enrol.group')}
              value={groupId}
              onChange={(e) => {
                setGroupId(e.target.value);
                setSearch('');
              }}
            >
              <option value="">{t('w18.nfc.enrol.chooseGroup')}</option>
              {(groups.data ?? [])
                .filter((g) => g.kind === 'repeat')
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </SelectField>
          </div>
          {groupId ? (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-800">{t('w18.nfc.enrol.people')}</legend>
              <TextField label={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
              {!members.data ? (
                <Spinner />
              ) : (
                <div className="grid max-h-64 gap-1 overflow-y-auto sm:grid-cols-2">
                  {members.data.items.map((m) => (
                    <CheckboxField
                      key={m.id}
                      label={m.fullName}
                      checked={m.id in chosen}
                      onChange={(e) => {
                        const next = { ...chosen };
                        if (e.target.checked) next[m.id] = m.fullName;
                        else delete next[m.id];
                        setChosen(next);
                      }}
                    />
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-600">{t('w18.nfc.enrol.chosen', { count })}</p>
            </fieldset>
          ) : null}
          <ErrorLine error={start.error} />
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="primary" disabled={start.isPending || !deviceId || count === 0 || count > 200} onClick={() => start.mutate()}>
              {t('w18.nfc.enrol.start')}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** Manage -> NFC cards: every card, filters, import from CSV and "Enrol cards on a kiosk" (contract nfc-cards s4). */
export function NfcCardsPage() {
  const canManage = useCan('nfc_cards.manage');
  const list = useListState();
  const [status, setStatus] = useState<Status>('all');
  const [dialog, setDialog] = useState<'import' | 'enrol' | null>(null);
  const query = {
    page: list.page,
    pageSize: PAGE_SIZE,
    search: list.search || undefined,
    status,
  };
  const q = useQuery({
    queryKey: [...CARDS_KEY, 'list', query],
    queryFn: () => api.get(NFC, { query, schema: NfcCardList }),
    placeholderData: keepPreviousData,
  });
  return (
    <div>
      <PageHeader
        title={t('w18.nfc.title')}
        description={t('w18.nfc.description')}
        actions={
          canManage ? (
            <>
              <Button onClick={() => setDialog('import')}>{t('w18.nfc.import.open')}</Button>
              <Button variant="primary" onClick={() => setDialog('enrol')}>
                {t('w18.nfc.enrol.open')}
              </Button>
            </>
          ) : undefined
        }
      />
      <FilterBar columns="sm:grid-cols-[1fr_12rem]">
        <TextField label={t('common.search')} value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as Status);
            list.resetPage();
          }}
        >
          <option value="all">{t('w18.nfc.status.all')}</option>
          <option value="active">{t('w18.nfc.status.active')}</option>
          <option value="blocked">{t('w18.nfc.status.blocked')}</option>
          <option value="lost">{t('w18.nfc.status.lost')}</option>
          <option value="retired">{t('w18.nfc.status.retired')}</option>
        </SelectField>
      </FilterBar>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <>
          <TableWrap caption={t('w18.nfc.title')}>
            <thead className="bg-slate-50">
              <tr>
                <Th>{t('w18.nfc.person')}</Th>
                <Th>{t('w18.nfc.label')}</Th>
                <Th>{t('w18.nfc.card')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('w18.nfc.lastUsedCol')}</Th>
                {canManage ? <Th>{t('common.actions')}</Th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? <EmptyRow cols={canManage ? 6 : 5} /> : null}
              {q.data.items.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{c.memberName}</td>
                  <td className="px-4 py-2.5">{c.label}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{hintText(c.hint)}</td>
                  <td className="px-4 py-2.5">
                    <CardStatus card={c} />
                  </td>
                  <td className="px-4 py-2.5">{c.lastUsedAt ? formatDateTime(c.lastUsedAt) : t('w18.nfc.neverUsed')}</td>
                  {canManage ? (
                    <td className="px-4 py-2.5">
                      <CardActions card={c} canManage={canManage} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={q.data.total} onPage={list.setPage} />
        </>
      )}
      {dialog === 'import' ? <ImportDialog onClose={() => setDialog(null)} /> : null}
      {dialog === 'enrol' ? <EnrolDialog onClose={() => setDialog(null)} /> : null}
    </div>
  );
}
